import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { Button } from './ui/button';
import { useImageFallback } from '../utils/useImageFallback';
import loadLevel from '../loadLevel.ts';
import { playBeatstage } from '../playBeatstage.ts';

export interface Song {
  id: string;
  name: string;
  image: string;
}

const modes = ['Keyboard', 'Mouse'];

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function useFocusTrap(active: boolean, ref: React.RefObject<HTMLElement>) {
  useEffect(() => {
    if (!active || !ref.current) return;
    const node = ref.current;
    const focusable = node.querySelectorAll<HTMLElement>(
      'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])',
    );
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    first?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [active, ref]);
}

interface SongCardProps {
  song: Song;
  className?: string;
  tiltEnabled?: boolean;
  onPlay?: (song: Song) => void;
}
const SongCard = forwardRef<HTMLDivElement, SongCardProps>(
  ({ song, className, tiltEnabled = true, onPlay }, ref) => {
  const [flipped, setFlipped] = useState(false);
  const [mode, setMode] = useState('Keyboard');
  const [loading, setLoading] = useState(false);
  const backRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });

  useFocusTrap(flipped, backRef);

  useEffect(() => {
    if (!flipped) return;
    const handle = (e: MouseEvent) => {
      if (!backRef.current?.contains(e.target as Node)) {
        setFlipped(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [flipped]);

  const handleClose = () => setFlipped(false);

  const handleImageError = useImageFallback();

  const handleMouseMove = (evt: React.MouseEvent<HTMLDivElement, MouseEvent>): void => {
    const target = evt.currentTarget;
    const rect = target.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;
    const midX = rect.width / 2;
    const midY = rect.height / 2;
    const rotY = clamp(((x - midX) / midX) * 10, -8, 8);
    const rotX = clamp(((midY - y) / midY) * 10, -8, 8);
    setTilt({ x: rotX, y: rotY });
  };

  const clearTilt = (): void => setTilt({ x: 0, y: 0 });

  const handlePlay = async (): Promise<void> => {
    if (onPlay) {
      onPlay(song);
      return;
    }
    const artist = (song as any).artist ?? (song as any).artistId;
    const levelId = artist ? `${artist}/${song.id}` : song.id;
    setLoading(true);
    try {
      const { data, audio } = await loadLevel(levelId);
      const encoded = btoa(JSON.stringify(data));
      const audioBlob = new Blob([audio]);
      await playBeatstage(encoded, audioBlob, document.body, false, {
        metadata: { mode },
      } as any);
    } catch (err) {
      console.error(err);
      alert('Failed to load level');
    } finally {
      setLoading(false);
    }
  };

  const front = (
    <div
      className="absolute inset-0 overflow-hidden rounded-3xl [backface-visibility:hidden]"
      onClick={() => setFlipped(true)}
    >
      <img
        src={song.image}
        alt={song.name}
        loading="lazy"
        className="h-full w-full object-cover"
        draggable={false}
        onError={handleImageError}
      />
      <div className="absolute bottom-2 left-2 right-2 rounded-2xl border border-hairline bg-black/55 px-3 py-1 text-center text-sm font-semibold text-white backdrop-blur-md">
        {song.name}
      </div>
    </div>
  );

  const back = (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center gap-4 rounded-3xl glass p-4 [backface-visibility:hidden] [transform:rotateY(180deg)]"
      ref={backRef}
    >
      <button
        type="button"
        onClick={handleClose}
        className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-hairline bg-black/50 text-white/90 hover:bg-black/60 focus:outline-none focus:ring-2 focus:ring-accent"
        aria-label="Close"
      >
        ✕
      </button>
      <div className="space-y-2">
        <div className="text-center text-sm">Mode</div>
        <div className="flex gap-2">
          {modes.map((m) => (
            <Button
              key={m}
              variant={m === mode ? 'default' : 'outline'}
              size="sm"
              onClick={() => setMode(m)}
            >
              {m}
            </Button>
          ))}
        </div>
      </div>
      <Button
        className="w-full focus:ring-2 focus:ring-accent"
        onClick={handlePlay}
        disabled={loading}
      >
        Play (Slow)
      </Button>
      <Button
        className="w-full"
        variant="outline"
        onClick={() => alert('Room feature coming soon')}
      >
        Start Room
      </Button>
    </div>
  );

  return (
    <div ref={ref} className={clsx('relative aspect-[9/16] w-[32rem]', className)}>
        <motion.div
          className={clsx('absolute inset-0 h-full w-full', tiltEnabled && '[perspective:1000px]')}
          {...(tiltEnabled
            ? {
                whileHover: {
                  scale: 1.04,
                  translateZ: 28,
                  boxShadow: '0 25px 50px rgba(0,0,0,0.35)',
                  transition: { duration: 0.2, ease: 'easeInOut' },
                },
                onMouseMove: handleMouseMove,
                onMouseLeave: clearTilt,
                style: {
                  transformStyle: 'preserve-3d',
                  rotateX: tilt.x,
                  rotateY: tilt.y,
                  willChange: 'transform, opacity',
                  transition: 'transform 0.3s ease-in-out',
                },
              }
            : {})}
        >
          <motion.div
            className="absolute inset-0 h-full w-full"
            animate={{ rotateY: flipped ? 180 : 0 }}
            style={{ transformStyle: 'preserve-3d', willChange: 'transform, opacity' }}
            transition={{ duration: 0.4, ease: 'easeInOut' }}
          >
            {front}
            {back}
          </motion.div>
        </motion.div>
        <AnimatePresence initial={false} mode="wait">
          {flipped ? (
            <motion.div
              key="back"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="absolute inset-0"
            >
              {back}
            </motion.div>
          ) : (
            <motion.div
              key="front"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              className="absolute inset-0"
            >
              {front}
            </motion.div>
          )}
        </AnimatePresence>
    </div>
  );
});

export default SongCard;
